/**
 * The wuxia village (โลกยุทธภพเก้ากระบี่, client v1's hub; docs/archive/CLIENT_V1_PLAN.md A3 S2): the layout rows the rules
 * read, in the village's flat world metres (x east, z south; the spawn looks −z). The client draws from the same
 * rows (render3d/village world.ts). Research: docs/research/WUXIA_WORLD.md §1.
 * - `VILLAGE_PATHS`: path centre lines and widths (the main path north through the paifang to the courtyard gate, the
 *   east branch over the bridge, the lane from the moon gate).
 * - `VILLAGE_YARD`: the training yard's packed earth (a fat stroke).
 * - `VILLAGE_WATER`: capsules (segment + radius): the stream, the pond, the outflow (`waterDepth` in village/layout).
 * - `VILLAGE_SPAWN`, `VILLAGE_EDGE` (the soft edge circle), `VILLAGE_ARENA` (the world boss's arena, kept clear).
 * Solids (walls, roofs, the bridge deck, plinths, rocks, trunks) are still built with the kit's geometry in the client
 * (`physics.ts` `Solid` rows); they move here as rows in S3.
 */

export type VillageP2 = readonly [number, number];

export const VILLAGE_PATHS: readonly { readonly pts: readonly VillageP2[]; readonly w: number }[] = [
  { pts: [[6, 44], [4.5, 30], [3, 20], [2.6, 14], [1.5, 6], [0, -2], [-3, -8], [-6, -14], [-7, -19.5]], w: 4.3 },
  { pts: [[0, -2], [6, -9], [13, -14], [18.25, -17.1], [24, -20], [34, -24], [42, -27]], w: 4.0 },
  { pts: [[2.6, -27.2], [8, -25], [12, -19], [13, -14]], w: 3.0 },
];

export const VILLAGE_YARD: { readonly pts: readonly VillageP2[]; readonly w: number } = { pts: [[-17.5, -7], [-11, -7]], w: 8 };

export interface VillageWater {
  readonly a: VillageP2;
  readonly b: VillageP2;
  readonly r: number;
}

export const VILLAGE_WATER: readonly VillageWater[] = (() => {
  const out: VillageWater[] = [];
  const chain = (pts: VillageP2[], r: number): void => {
    for (let i = 0; i + 1 < pts.length; i++) out.push({ a: pts[i]!, b: pts[i + 1]!, r });
  };
  chain([[31, -48], [23, -32], [18.6, -21], [18.1, -14], [19.5, -6], [21, -1]], 2.0);
  out.push({ a: [20.5, 0], b: [24, 0.5], r: 4.2 });
  chain([[24, 1], [27, 9], [31, 16], [36, 24], [40, 34]], 1.8);
  return out;
})();

/** Where the player starts (x, z). */
export const VILLAGE_SPAWN: VillageP2 = [3, 24];
/** The soft edge: centre and radius (m). */
export const VILLAGE_EDGE = { x: 4, z: -6, r: 46 } as const;
/** The world boss's arena (STARTER.arena is its centre): kept clear of trees, rocks and plants. */
export const VILLAGE_ARENA = { x: -24, z: 14, r: 12.5 } as const;
/** Water deeper than this (m) cannot be stood in. */
export const VILLAGE_WATER_STAND = 0.15;
